import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { canonicalAdminRole, isBackendSuperadminRole } from "../contracts/backend/adminContract";
import { SERVICE_LOCATION_FALLBACKS } from "../contracts/backend/modelContract";

// src/auth/adminSession.js
// Single source of truth sesi admin + resolver base URL + safe JSON fetch

const PROD_API_HOST_HTTP = "http://apiklik.bkkpangkalpinang.com";
const PROD_API_HOST_HTTPS = "https://apiklik.bkkpangkalpinang.com";
const PROD_FE_HOST = "https://klik.bkkpangkalpinang.com";

// ===== Session keys
const KEY_TOKEN = "auth.token";
const KEY_USER = "auth.user";
const KEY_API = "api.base"; // root tanpa /api
const KEY_DEVICE_ID = "auth.device_id";

// legacy token keys (biar session lama tetap kebaca)
const LEGACY_TOKEN_KEYS = [
  "kbkk.admin.access_token",
  "kbkk.admin.token",
  "access_token",
  "token",
  "auth_token",
];

const DEFAULT_REQ_TIMEOUT_MS = 15000;

/* ============================================================================
 * Helpers kecil
 * ========================================================================== */
function stripSlash(s) {
  return String(s || "").trim().replace(/\/+$/, "");
}

export function ensureApiSuffix(base) {
  if (!base) return "";
  const clean = stripSlash(base);
  return clean.endsWith("/api") ? clean : `${clean}/api`;
}

function toRootFromApi(apiBase) {
  return stripSlash(String(apiBase || "")).replace(/\/api$/i, "");
}

function joinUrl(base, path) {
  const b = stripSlash(base);
  const p = String(path || "").replace(/^\/+/, "");
  return `${b}/${p}`;
}

function normalizePath(path) {
  return String(path || "").replace(/^\/+/, "");
}

function withTimeout(ms = 3500) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  return {
    signal: ctrl.signal,
    done: () => clearTimeout(timer),
  };
}

function isJsonContentType(contentType) {
  const ct = String(contentType || "").toLowerCase();
  return ct.includes("application/json") || ct.includes("+json");
}

function isProbablyHtml(text) {
  const s = String(text || "").trim().toLowerCase();
  return (
    s.startsWith("<!doctype") ||
    s.startsWith("<html") ||
    s.startsWith("<head") ||
    s.startsWith("<body") ||
    s.startsWith("<")
  );
}

function safeJsonParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function pickMessageFromParsed(parsed) {
  if (!parsed || typeof parsed !== "object") return "";
  return (
    parsed.message ||
    parsed.error ||
    parsed.msg ||
    parsed.detail ||
    parsed.errors?.message ||
    ""
  );
}

function makeNonJsonError(url, status, bodyText) {
  const preview = String(bodyText || "").slice(0, 700);
  const err = new Error(
    `Response bukan JSON dari ${url}. ${
      isProbablyHtml(preview)
        ? "Server mengembalikan HTML, bukan JSON."
        : "Server tidak mengembalikan JSON yang valid."
    }`
  );
  err.status = status || 0;
  err.body = preview;
  err.isNonJson = true;
  err.url = url;
  return err;
}

function getStoredAuthToken() {
  const primary = localStorage.getItem(KEY_TOKEN);
  if (primary) return primary;

  for (const k of LEGACY_TOKEN_KEYS) {
    const v = localStorage.getItem(k);
    if (v) return v;
  }

  return "";
}

function clearLegacyTokens() {
  for (const k of LEGACY_TOKEN_KEYS) {
    localStorage.removeItem(k);
  }
}

/* ============================================================================
 * Session helpers
 * ========================================================================== */
export function saveSession({ token, user, apiBase }) {
  if (token) {
    localStorage.setItem(KEY_TOKEN, String(token));
    // kompat lama
    localStorage.setItem("kbkk.admin.access_token", String(token));
  }

  if (user) localStorage.setItem(KEY_USER, JSON.stringify(user));
  if (apiBase) localStorage.setItem(KEY_API, toRootFromApi(apiBase));
}

export function clearSession() {
  localStorage.removeItem(KEY_TOKEN);
  localStorage.removeItem(KEY_USER);
  clearLegacyTokens();
  // KEY_API sengaja tidak dihapus supaya base yang benar tetap dipakai
}

export function getSession() {
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem(KEY_USER) || "null");
  } catch {
    user = null;
  }

  const token = getStoredAuthToken() || "";

  return {
    token,
    access_token: token,
    authToken: token,
    token_type: "Bearer",
    user,
    apiBase: localStorage.getItem(KEY_API) || "",
  };
}

/* ============================================================================
 * Device ID
 * ========================================================================== */
function genId() {
  return `dev_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

export function getOrCreateDeviceId() {
  let id = localStorage.getItem(KEY_DEVICE_ID) || "";
  if (!id) {
    id = genId();
    localStorage.setItem(KEY_DEVICE_ID, id);
  }
  return id;
}

/* ============================================================================
 * Role helpers
 * ========================================================================== */
export function normalizeRoleSlug(value) {
  return canonicalAdminRole(value);
}

export function isAllowedAdminRole(slug) {
  const normalized = normalizeRoleSlug(slug);
  return !!normalized && Object.prototype.hasOwnProperty.call(ROLE_MENU_ACCESS, normalized);
}

export function extractRole(user) {
  if (!user) return "";
  if (user.effective_role) return normalizeRoleSlug(user.effective_role);
  if (user.effectiveRole) return normalizeRoleSlug(user.effectiveRole);
  if (user.role_slug) return normalizeRoleSlug(user.role_slug);
  if (user.roleSlug) return normalizeRoleSlug(user.roleSlug);
  if (user.role && typeof user.role === "object" && user.role.slug) {
    return normalizeRoleSlug(user.role.slug);
  }
  if (user.role && typeof user.role === "object" && user.role.name) {
    return normalizeRoleSlug(user.role.name);
  }
  if (typeof user.role === "string") return normalizeRoleSlug(user.role);
  return "";
}

/* ============================================================================
 * Resolve API base
 * ========================================================================== */
function envApiBaseRoot() {
  const fromCRA =
    (typeof process !== "undefined" &&
      process.env &&
      (process.env.REACT_APP_API_BASE_URL ||
        process.env.REACT_APP_API_BASEURL)) ||
    "";

  const fromViteCompat =
    (typeof process !== "undefined" &&
      process.env &&
      process.env.VITE_API_BASE_URL) ||
    "";

  const fromWin =
    (typeof window !== "undefined" &&
      window.__ENV__ &&
      window.__ENV__.API_BASE) ||
    "";

  const picked = stripSlash(fromCRA || fromViteCompat || fromWin);
  return picked ? toRootFromApi(picked) : "";
}

function preferredApiRootSync() {
  const stored = stripSlash(localStorage.getItem(KEY_API) || "");
  if (stored) return stored;

  const envBase = envApiBaseRoot();
  if (envBase) return envBase;

  if (typeof window !== "undefined") {
    const { origin, port, protocol } = window.location;
    const isDevPort = ["3000", "5173", "4173"].includes(port || "");

    if (isDevPort) return stripSlash(`${protocol}//127.0.0.1:8000`);

    if (stripSlash(origin) === stripSlash(PROD_FE_HOST)) {
      return stripSlash(PROD_API_HOST_HTTPS);
    }

    return stripSlash(PROD_API_HOST_HTTPS);
  }

  return stripSlash(PROD_API_HOST_HTTPS || "http://127.0.0.1:8000");
}

export function getApiBaseForDisplay() {
  const cached = stripSlash(localStorage.getItem(KEY_API) || "");
  return cached || preferredApiRootSync();
}

export function getApiBaseForDisplaySafe() {
  return getApiBaseForDisplay();
}

export function noteWorkingApiBase(apiBase) {
  if (!apiBase) return;
  const root = toRootFromApi(apiBase);
  localStorage.setItem(KEY_API, root);
  _workingRootCache = stripSlash(root);
}

function makeApiRootCandidates() {
  const out = [];
  const add = (base) => {
    if (!base) return;
    out.push(stripSlash(toRootFromApi(base)));
  };

  // stored/env dulu
  add(localStorage.getItem(KEY_API) || "");
  add(envApiBaseRoot());

  // prioritas ke backend baru
  add(PROD_API_HOST_HTTPS);
  add(PROD_API_HOST_HTTP);
  add(`${PROD_API_HOST_HTTPS}/public`);
  add(`${PROD_API_HOST_HTTP}/public`);

  if (typeof window !== "undefined") {
    const { origin, port, hostname, protocol } = window.location;
    const isDevPort = ["3000", "5173", "4173"].includes(port || "");
    const isLocalHost = /^(localhost|127\.0\.0\.1)$/i.test(hostname || "");
    const currentOrigin = stripSlash(origin);

    if (isDevPort) {
      add(`${protocol}//127.0.0.1:8000`);
      add("http://localhost:8000");
      if (!isLocalHost) add(`${protocol}//${hostname}:8000`);
    } else {
      // FE dedicated klik.* -> jangan jadikan FE origin kandidat utama API
      if (currentOrigin !== stripSlash(PROD_FE_HOST)) {
        add(origin);
        add(`${origin}/public`);
        add(`${origin}/klik-bkk-api/public`);
        add(`${origin}/bkk-api/public`);
      }

      // derivasi klik.* -> apiklik.*
      try {
        if (hostname && /^klik\./i.test(hostname)) {
          const derived = `${protocol}//${hostname.replace(
            /^klik\./i,
            "apiklik."
          )}`;
          add(derived);
          add(`${derived}/public`);
        }
      } catch {
        // ignore
      }
    }

    if (Array.isArray(window.__API_BASES__)) {
      window.__API_BASES__.forEach(add);
    }
    if (window.__ENV__?.API_BASE) add(window.__ENV__.API_BASE);
  }

  return [...new Set(out.filter(Boolean))];
}

function preconnectCandidates(roots) {
  try {
    if (typeof document === "undefined") return;

    const seen = new Set();
    roots.forEach((r) => {
      try {
        const u = new URL(r, window.location.origin);
        const org = u.origin;
        if (seen.has(org)) return;
        seen.add(org);

        const dns = document.createElement("link");
        dns.rel = "dns-prefetch";
        dns.href = org;

        const pre = document.createElement("link");
        pre.rel = "preconnect";
        pre.href = org;
        pre.crossOrigin = "anonymous";

        document.head.appendChild(dns);
        document.head.appendChild(pre);
      } catch {
        // ignore
      }
    });
  } catch {
    // ignore
  }
}

async function isUsablePingResponse(res) {
  if (!res || !res.ok) return false;

  const contentType = res.headers.get("content-type") || "";
  const text = await res.text().catch(() => "");

  if (isProbablyHtml(text)) return false;

  if (isJsonContentType(contentType)) {
    return safeJsonParse(text) !== null;
  }

  const short = String(text || "").trim().toLowerCase();
  return ["ok", "pong", "alive", "success"].includes(short);
}

async function resolveWorkingRootBase() {
  const candidates = makeApiRootCandidates();
  preconnectCandidates(candidates);

  const preferred = preferredApiRootSync();
  const ordered = [
    preferred,
    ...candidates.filter((c) => stripSlash(c) !== stripSlash(preferred)),
  ];

  for (const root of ordered) {
    try {
      const apiBase = ensureApiSuffix(root);
      const pingUrl = joinUrl(apiBase, "ping");
      const { signal, done } = withTimeout(3500);

      const res = await fetch(pingUrl, {
        method: "GET",
        headers: { Accept: "application/json" },
        mode: "cors",
        credentials: "omit",
        signal,
      });

      done();

      if (await isUsablePingResponse(res)) {
        noteWorkingApiBase(root);
        return stripSlash(root);
      }
    } catch {
      // try next
    }
  }

  const fallback = stripSlash(PROD_API_HOST_HTTPS || preferred);
  noteWorkingApiBase(fallback);
  return fallback;
}

// single-flight cache
let _workingRootCache = "";
let _resolvingPromise = null;

export async function getWorkingRoot({ force = false } = {}) {
  const stored = stripSlash(localStorage.getItem(KEY_API) || "");

  if (!force && stored) {
    _workingRootCache = stored;
    return stored;
  }

  if (!force && _workingRootCache) return _workingRootCache;
  if (!force && _resolvingPromise) return _resolvingPromise;

  _resolvingPromise = resolveWorkingRootBase()
    .then((root) => {
      _workingRootCache = stripSlash(root);
      return _workingRootCache;
    })
    .finally(() => {
      _resolvingPromise = null;
    });

  return _resolvingPromise;
}

export async function resolveApiBase({ force = false } = {}) {
  const root = await getWorkingRoot({ force });
  return ensureApiSuffix(root);
}

/* ============================================================================
 * Low-level fetch
 * ========================================================================== */
async function doFetchOnce(root, path, options = {}) {
  const apiBase = ensureApiSuffix(root);
  const url = joinUrl(apiBase, normalizePath(path));

  const {
    timeoutMs = DEFAULT_REQ_TIMEOUT_MS,
    headers: optHeaders,
    body: optBody,
    mode,
    credentials,
    skipAuth = false,

    // internal flag
    __retriedBaseResolve,
    ...rest
  } = options;

  const headers = new Headers(optHeaders || {});
  if (!headers.has("Accept")) headers.set("Accept", "application/json");

  if (!skipAuth) {
    const token = getStoredAuthToken();
    if (token && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }

  let body = optBody;
  const isPlainObject =
    body &&
    typeof body === "object" &&
    !(body instanceof FormData) &&
    !(body instanceof Blob) &&
    !(body instanceof ArrayBuffer);

  if (isPlainObject) {
    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    body = JSON.stringify(body);
  }

  const { signal, done } = withTimeout(timeoutMs);

  try {
    const res = await fetch(url, {
      ...rest,
      method: rest.method || (body ? "POST" : "GET"),
      headers,
      body,
      signal,
      mode: mode || "cors",
      credentials: credentials || "omit",
    });

    return { res, url };
  } finally {
    done();
  }
}

async function doFetchJsonOnce(root, path, options = {}) {
  const { res, url } = await doFetchOnce(root, path, options);
  const contentType = (res.headers.get("content-type") || "").toLowerCase();
  const rawText = await res.text().catch(() => "");
  const trimmed = String(rawText || "").trim();

  if (res.status === 204 || res.status === 205) {
    return {
      res,
      url,
      contentType,
      rawText,
      parsed: null,
      isJson: true,
      isHtml: false,
    };
  }

  if (!trimmed && res.ok) {
    return {
      res,
      url,
      contentType,
      rawText,
      parsed: null,
      isJson: true,
      isHtml: false,
    };
  }

  const isHtml = isProbablyHtml(trimmed);
  let parsed = null;
  let isJson = false;

  if (isJsonContentType(contentType) || trimmed.startsWith("{") || trimmed.startsWith("[")) {
    parsed = safeJsonParse(trimmed);
    isJson = parsed !== null;
  }

  return {
    res,
    url,
    contentType,
    rawText,
    parsed,
    isJson,
    isHtml,
  };
}

function buildHttpError(result) {
  const { res, url, parsed, rawText } = result;
  const status = res?.status || 0;
  const statusText = res?.statusText || "";
  const bodyPreview = String(rawText || "").slice(0, 700);
  const messageFromJson = pickMessageFromParsed(parsed);

  let msg = `HTTP ${status}${statusText ? ` ${statusText}` : ""}`;

  if (messageFromJson) {
    msg += ` - ${messageFromJson}`;
  } else if (isProbablyHtml(bodyPreview)) {
    msg += " - Server mengembalikan HTML, bukan JSON.";
  } else if (bodyPreview) {
    msg += ` - ${bodyPreview}`;
  }

  const err = new Error(msg);
  err.status = status;
  err.url = url;
  err.body = bodyPreview;
  err.data = parsed;
  return err;
}

/* ============================================================================
 * Transient server retry
 * - hanya untuk request idempotent/read; POST/PUT/PATCH/DELETE tidak diulang
 *   otomatis agar tidak membuat duplikasi transaksi.
 * ========================================================================== */
function isSafeReadMethod(options = {}) {
  const method = String(options.method || "GET").toUpperCase();
  return ["GET", "HEAD", "OPTIONS"].includes(method);
}

function transientRetryDelayMs(res) {
  const raw = Number(res?.headers?.get?.("retry-after") || 0);
  const base = Number.isFinite(raw) && raw > 0 ? raw * 1000 : 1000;
  const jitter = 100 + Math.floor(Math.random() * 350);
  return Math.min(4000, Math.max(500, base + jitter));
}

function sleepMs(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/* ============================================================================
 * Public fetch helpers
 * ========================================================================== */
export async function apiFetch(path, options = {}) {
  const root1 = await getWorkingRoot({ force: false });
  let first = await doFetchOnce(root1, path, options);

  if (
    first.res.status === 503 &&
    isSafeReadMethod(options) &&
    !options.__retriedTransientDb
  ) {
    await sleepMs(transientRetryDelayMs(first.res));
    first = await doFetchOnce(root1, path, {
      ...options,
      __retriedTransientDb: true,
    });
  }

  if (first.res.status === 401 && !options.__retriedBaseResolve) {
    const root2 = await getWorkingRoot({ force: true });
    if (stripSlash(root2) !== stripSlash(root1)) {
      return doFetchOnce(root2, path, {
        ...options,
        __retriedBaseResolve: true,
      }).then((x) => x.res);
    }
  }

  return first.res;
}

export async function apiFetchJson(path, options = {}) {
  const root1 = await getWorkingRoot({ force: false });
  let result = await doFetchJsonOnce(root1, path, options);

  if (
    result.res.status === 503 &&
    isSafeReadMethod(options) &&
    !options.__retriedTransientDb
  ) {
    await sleepMs(transientRetryDelayMs(result.res));
    result = await doFetchJsonOnce(root1, path, {
      ...options,
      __retriedTransientDb: true,
    });
  }

  const shouldRetryBase =
    !options.__retriedBaseResolve &&
    (result.res.status === 401 || result.isHtml || !result.isJson);

  if (shouldRetryBase) {
    const root2 = await getWorkingRoot({ force: true });
    if (stripSlash(root2) !== stripSlash(root1)) {
      result = await doFetchJsonOnce(root2, path, {
        ...options,
        __retriedBaseResolve: true,
      });
    }
  }

  if (!result.res.ok) {
    throw buildHttpError(result);
  }

  if (!result.isJson) {
    throw makeNonJsonError(result.url, result.res.status, result.rawText);
  }

  return result.parsed;
}

/* ============================================================================
 * Auth API
 * ========================================================================== */
export async function adminLogin({ email, password, lokasi_id, lokasiId } = {}) {
  const device_name = getOrCreateDeviceId();

  const data = await apiFetchJson("auth/login", {
    method: "POST",
    body: { email, password, device_name, lokasi_id: lokasi_id ?? lokasiId ?? undefined },
    skipAuth: true,
  });

  if (!data?.token) {
    throw new Error(data?.message || "Login gagal");
  }

  const root = await getWorkingRoot({ force: false });
  saveSession({
    token: data.token,
    user: data.user,
    apiBase: root,
  });

  return data;
}

export async function adminLogout() {
  try {
    const res = await apiFetch("auth/logout", {
      method: "POST",
      skipAuth: false,
    });

    if (!res.ok && ![401, 419].includes(res.status)) {
      console.warn("Logout API not OK:", res.status);
    }
  } catch (e) {
    console.warn("Logout API error:", e);
  } finally {
    clearSession();
  }

  return true;
}

/* ==========================================================================
 * Admin role + lokasi helpers
 * ======================================================================== */

export const SERVICE_LOCATIONS = SERVICE_LOCATION_FALLBACKS;

export const DEFAULT_SERVICE_LOCATION_ID = 1;


export const ROLE_MENU_ACCESS = {
  superadmin_app: "*",
  registrasi: ["dashboard", "registrasi", "whatsapp", "laporan", "pengukuran", "tunda", "pembatalan", "bukutamu"],
  skrining: ["dashboard", "skrining", "whatsapp", "laporan", "pengukuran", "tunda", "pembatalan"],
  klinik: ["dashboard", "klinik", "whatsapp", "laporan", "pengukuran", "tunda", "pembatalan"],
  billing: ["dashboard", "billing", "whatsapp", "laporan", "pengukuran", "tunda", "pembatalan"],
  pembatalan: ["dashboard", "pembatalan"],
};

export function normalizeLocationId(value, fallback = DEFAULT_SERVICE_LOCATION_ID) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

export function parseLocationId(value) {
  if (value === null || value === undefined || value === "" || value === "all" || value === "semua") {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function getServiceLocation(value) {
  const id = normalizeLocationId(value);
  return SERVICE_LOCATIONS.find((loc) => Number(loc.id) === Number(id)) || SERVICE_LOCATIONS[0];
}

function pickUserRole(user) {
  if (!user || typeof user !== "object") return "";

  return (
    user.effective_role ||
    user.effectiveRole ||
    user.role_slug ||
    user.roleSlug ||
    user.role?.slug ||
    user.role?.name ||
    user.role ||
    user.role_name ||
    user.roleName ||
    ""
  );
}

function legacyRoleInferenceEnabled() {
  return (
    typeof process !== "undefined" &&
    process.env &&
    String(process.env.REACT_APP_ENABLE_LEGACY_ROLE_INFERENCE || "").toLowerCase() === "true"
  );
}

function inferOperationalRoleFromIdentity(user) {
  if (!legacyRoleInferenceEnabled()) return "";

  const haystack = `${user?.name || ""} ${user?.email || ""}`.toLowerCase();
  if (!haystack.trim()) return "";
  if (haystack.includes("super")) return "superadmin_app";
  if (haystack.includes("registrasi") || haystack.includes("pendaftaran")) return "registrasi";
  if (haystack.includes("skrining") || haystack.includes("screening")) return "skrining";
  if (haystack.includes("klinik") || haystack.includes("clinic") || haystack.includes("dokter")) return "klinik";
  if (haystack.includes("billing") || haystack.includes("kasir") || haystack.includes("bayar")) return "billing";
  if (haystack.includes("pembatalan") || haystack.includes("cancel")) return "pembatalan";
  return "";
}

export function getEffectiveAdminRole(user) {
  // Backend Laravel (User model/AuthController) adalah source of truth.
  // Inference nama/email hanya tersedia sebagai opt-in compatibility flag.
  const explicit = normalizeRoleSlug(user?.effective_role || user?.effectiveRole);
  if (explicit) return explicit;

  const fromUser = normalizeRoleSlug(pickUserRole(user));
  if (fromUser) return fromUser;

  return inferOperationalRoleFromIdentity(user);
}

export function isSuperAdminRole(role) {
  return isBackendSuperadminRole(role);
}

export function isAdminPortalRole(role) {
  return Object.prototype.hasOwnProperty.call(ROLE_MENU_ACCESS, normalizeRoleSlug(role));
}

export function isMenuAllowedForRole(menuKey, role) {
  const r = normalizeRoleSlug(role);
  const allowed = ROLE_MENU_ACCESS[r];
  if (allowed === "*") return true;
  if (!Array.isArray(allowed)) return false;
  return allowed.includes(menuKey);
}

export function filterMenusByAdminRole(menus, role) {
  const r = normalizeRoleSlug(role);
  const allowed = ROLE_MENU_ACCESS[r];
  if (allowed === "*") return menus;
  if (!Array.isArray(allowed)) return [];

  return (menus || [])
    .filter((item) => allowed.includes(item.key))
    .map((item) => ({
      ...item,
      children: Array.isArray(item.children) ? item.children : undefined,
    }));
}

function storageGet(key) {
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function storageSet(key, value) {
  try {
    localStorage.setItem(key, String(value));
    sessionStorage.setItem(key, String(value));
  } catch {
    // ignore
  }
}

export function getDefaultLoginLocationId() {
  return normalizeLocationId(storageGet("kbkk.admin.selected_lokasi_id") || DEFAULT_SERVICE_LOCATION_ID);
}

export function applyAdminLoginScope(user, { lokasiId, role } = {}) {
  const effectiveRole = normalizeRoleSlug(role) || getEffectiveAdminRole(user);
  const isSuper = isSuperAdminRole(effectiveRole);

  const requestedLocationId = parseLocationId(
    lokasiId ?? user?.admin_selected_lokasi_id ?? user?.lokasi_id
  );
  const accountLocationId = parseLocationId(user?.lokasi_id);

  // Non-superadmin hanya boleh memakai lokasi dari akun backend. Jangan fallback ke lokasi 1.
  const selectedLocationId = isSuper
    ? (requestedLocationId ?? DEFAULT_SERVICE_LOCATION_ID)
    : accountLocationId;
  const loc = selectedLocationId ? getServiceLocation(selectedLocationId) : null;

  const scopedUser = {
    ...(user || {}),
    effective_role: effectiveRole,
    role_slug: effectiveRole,
    admin_selected_lokasi_id: selectedLocationId,
    admin_selected_lokasi_nama: loc?.name || null,
    admin_selected_lokasi_short: loc?.shortName || null,
    lokasi_scope: isSuper ? "all" : "single",
    accessible_lokasi_ids: isSuper
      ? SERVICE_LOCATIONS.map((x) => Number(x.id))
      : selectedLocationId
      ? [selectedLocationId]
      : [],

    // Untuk kompatibilitas halaman lama, tetapi tidak mengarang lokasi bila akun kosong.
    lokasi_id: isSuper ? (user?.lokasi_id ?? null) : selectedLocationId,
    lokasi_nama: isSuper
      ? (user?.lokasi_nama ?? "Semua Lokasi Layanan")
      : (loc?.name || user?.lokasi_nama || null),
  };

  if (selectedLocationId) {
    storageSet("kbkk.admin.selected_lokasi_id", selectedLocationId);
    storageSet("kbkk.admin.selected_lokasi_nama", loc?.name || "");
  }
  storageSet("kbkk.admin.effective_role", effectiveRole);
  storageSet("kbkk.admin.lokasi_scope", scopedUser.lokasi_scope);

  return scopedUser;
}

export function getAdminScopeInfo() {
  let session = {};
  try {
    session = typeof getSession === "function" ? getSession() || {} : {};
  } catch {
    session = {};
  }

  const user = session.user || {};
  const role = normalizeRoleSlug(storageGet("kbkk.admin.effective_role")) || getEffectiveAdminRole(user);
  const isSuper = isSuperAdminRole(role);
  const accountLocationId = parseLocationId(user.lokasi_id);
  const requestedLocationId = parseLocationId(
    user.admin_selected_lokasi_id ??
      session.lokasi_id ??
      storageGet("kbkk.admin.selected_lokasi_id")
  );

  // Superadmin boleh memiliki pilihan UI; tanpa pilihan default dropdown tetap lokasi 1.
  // Non-superadmin tidak pernah mengambil lokasi dari cache/browser.
  const selectedLocationId = isSuper
    ? (requestedLocationId ?? DEFAULT_SERVICE_LOCATION_ID)
    : accountLocationId;
  const selectedLocation = selectedLocationId ? getServiceLocation(selectedLocationId) : null;

  return {
    user,
    token: session.token || session.access_token || session.authToken || "",
    role,
    roleLabel:
      role === "superadmin_app"
        ? "Superadmin"
        : role === "registrasi"
        ? "Registrasi"
        : role === "skrining"
        ? "Skrining"
        : role === "klinik"
        ? "Klinik"
        : role === "billing"
        ? "Billing"
        : role === "pembatalan"
        ? "Pembatalan"
        : role || "-",
    isSuperAdmin: isSuper,
    lokasiId: isSuper ? null : accountLocationId,
    selectedLocationId,
    selectedLocation,
    locationLabel: isSuper
      ? "Semua Lokasi"
      : selectedLocation?.name || "Lokasi belum ditetapkan",
    hasValidLocation: isSuper || Boolean(accountLocationId),
    accessibleLokasiIds: isSuper
      ? SERVICE_LOCATIONS.map((x) => Number(x.id))
      : accountLocationId
      ? [accountLocationId]
      : [],
  };
}

export function addQueryParam(path, key, value) {
  if (!path || value === undefined || value === null || value === "") return path;
  const raw = String(path);
  const hashIndex = raw.indexOf("#");
  const beforeHash = hashIndex >= 0 ? raw.slice(0, hashIndex) : raw;
  const hash = hashIndex >= 0 ? raw.slice(hashIndex) : "";
  const sep = beforeHash.includes("?") ? "&" : "?";
  return `${beforeHash}${sep}${encodeURIComponent(key)}=${encodeURIComponent(String(value))}${hash}`;
}

export function addAdminLocationQuery(path, { includeSuperadmin = false } = {}) {
  const raw = String(path || "");
  if (!raw) return raw;

  // Tidak mengubah URL absolute non-admin atau endpoint public.
  const target = raw.replace(/^https?:\/\/[^/]+/i, "");
  if (!target.startsWith("/admin/")) return raw;

  if (/[?&]lokasi_id=/i.test(raw)) return raw;

  const scope = getAdminScopeInfo();
  if (scope.isSuperAdmin && !includeSuperadmin) return raw;
  const lokasiId = scope.isSuperAdmin ? scope.selectedLocationId : scope.lokasiId;
  return lokasiId ? addQueryParam(raw, "lokasi_id", lokasiId) : raw;
}


export function useAdminRoleGuard(menuKey) {
  const navigate = useNavigate();

  useEffect(() => {
    const scope = getAdminScopeInfo();
    if (!scope.token) return;
    if (!isMenuAllowedForRole(menuKey, scope.role)) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [navigate, menuKey]);
}
